/**
 * EffectsPanel Component
 * 
 * Expandable panel attached to each instrument row for managing audio effects.
 * Displays effects chain slots and provides controls for adding, configuring,
 * and removing effects.
 */

import { useCallback, useMemo } from 'react';
import type { EffectInstance, EffectType } from '../../services/effects/EffectsManager';
import { EffectsManager } from '../../services/effects/EffectsManager';
import { EffectSlot } from './EffectSlot';
import './EffectsPanel.css';

/**
 * Props for EffectsPanel component
 */
export interface EffectsPanelProps {
  /** Row index */
  row: number;
  /** Whether the panel is expanded */
  isExpanded: boolean;
  /** Callback to toggle panel expansion */
  onToggle: () => void;
  /** Current effects chain for this row */
  effectsChain: EffectInstance[];
  /** Callback when effects chain changes */
  onEffectsChange: (effects: EffectInstance[]) => void;
}

/**
 * EffectsPanel component
 * 
 * Renders an expandable effects panel with:
 * - Header with expand/collapse toggle, preset selector, and bypass all button
 * - Effects chain container with 3 effect slots
 * - Support for adding, configuring, bypassing, and removing effects
 * 
 * @param props - Component props
 * @returns EffectsPanel component
 */
export function EffectsPanel({
  row,
  isExpanded,
  effectsChain,
  onEffectsChange,
}: EffectsPanelProps) {

  /**
   * Create EffectsManager instance to get available effects
   */
  const effectsManager = useMemo(() => new EffectsManager(), []);

  /**
   * Get available effect definitions
   */
  const availableEffects = useMemo(() => {
    return effectsManager.getAllEffectDefinitions();
  }, [effectsManager]);

  /**
   * Handle bypass all effects
   */
  const handleBypassAll = useCallback(() => {
    const allBypassed = effectsChain.every(effect => effect.bypassed);
    const updatedChain = effectsChain.map(effect => ({
      ...effect,
      bypassed: !allBypassed,
    }));
    onEffectsChange(updatedChain);
  }, [effectsChain, onEffectsChange]);

  /**
   * Check if all effects are bypassed
   */
  const allBypassed = effectsChain.length > 0 && effectsChain.every(effect => effect.bypassed);

  // Don't render anything if not expanded
  if (!isExpanded) {
    return null;
  }

  return (
    <div className="effects-panel-wrapper">
      {/* Effects Panel Header - Only show when there are effects */}
      {effectsChain.length > 0 && (
        <div className="effects-panel-header">
          {/* Bypass All Toggle */}
          <button
            className={`effects-bypass-all-btn ${allBypassed ? 'bypassed' : ''}`}
            onClick={handleBypassAll}
            aria-label={`${allBypassed ? 'Enable' : 'Bypass'} all effects for row ${row + 1}`}
            type="button"
          >
            <i className="fas fa-power-off" aria-hidden="true"></i>
            <span className="effects-bypass-text">
              {allBypassed ? 'Bypassed' : 'Active'}
            </span>
          </button>
        </div>
      )}

      {/* Effects Chain Container (Expandable) */}
      {isExpanded && (
        <div className="effects-chain-container">
          <div className="effects-chain">
            {/* Render effect slots progressively - only show next empty slot after previous is filled */}
            {/* Support up to 5 effect slots */}
            {[0, 1, 2, 3, 4].map((slot) => {
              const effect = effectsChain.find(e => e.slot === slot) || null;
              
              // Only show this slot if:
              // 1. It's slot 0 (always show first slot)
              // 2. It has an effect in it
              // 3. The previous slot has an effect (show next empty slot)
              if (slot === 0) {
                // Always show first slot
                return (
                  <EffectSlot
                    key={slot}
                    row={row}
                    slot={slot}
                    effect={effect}
                    availableEffects={availableEffects}
                    onEffectSelect={(type: EffectType) => {
                      // Get effect definition to use default parameters
                      const effectDef = effectsManager.getEffectDefinition(type);
                      const defaultParams: Record<string, number> = {};
                      
                      if (effectDef) {
                        effectDef.parameters.forEach(param => {
                          defaultParams[param.id] = param.default;
                        });
                      }
                      
                      // Create new effect instance
                      const newEffect: EffectInstance = {
                        id: `${type}-${slot}-${Date.now()}-${Math.random().toString(36).substring(2, 11)}`,
                        type,
                        parameters: defaultParams,
                        bypassed: false,
                        slot,
                      };
                      
                      // Replace or add effect in this slot
                      const updatedChain = effectsChain.filter(e => e.slot !== slot);
                      onEffectsChange([...updatedChain, newEffect].sort((a, b) => a.slot - b.slot));
                    }}
                    onParameterChange={(parameterId: string, value: number) => {
                      if (!effect) return;
                      
                      const updatedChain = effectsChain.map(e =>
                        e.id === effect.id
                          ? { ...e, parameters: { ...e.parameters, [parameterId]: value } }
                          : e
                      );
                      onEffectsChange(updatedChain);
                    }}
                    onParametersChange={(parameters: Record<string, number>) => {
                      if (!effect) return;
                      
                      const updatedChain = effectsChain.map(e =>
                        e.id === effect.id
                          ? { ...e, parameters: { ...e.parameters, ...parameters } }
                          : e
                      );
                      onEffectsChange(updatedChain);
                    }}
                    onBypass={(bypassed: boolean) => {
                      if (!effect) return;
                      
                      const updatedChain = effectsChain.map(e =>
                        e.id === effect.id ? { ...e, bypassed } : e
                      );
                      onEffectsChange(updatedChain);
                    }}
                    onRemove={() => {
                      if (!effect) return;
                      
                      const updatedChain = effectsChain.filter(e => e.id !== effect.id);
                      onEffectsChange(updatedChain);
                    }}
                  />
                );
              }
              
              // For slots 1 and 2, only show if previous slot is filled
              const previousSlot = slot - 1;
              const previousEffect = effectsChain.find(e => e.slot === previousSlot);
              
              if (!previousEffect && !effect) {
                // Previous slot is empty and this slot is empty - don't show
                return null;
              }
              
              return (
                <EffectSlot
                  key={slot}
                  row={row}
                  slot={slot}
                  effect={effect}
                  availableEffects={availableEffects}
                  onEffectSelect={(type: EffectType) => {
                    // Get effect definition to use default parameters
                    const effectDef = effectsManager.getEffectDefinition(type);
                    const defaultParams: Record<string, number> = {};
                    
                    if (effectDef) {
                      effectDef.parameters.forEach(param => {
                        defaultParams[param.id] = param.default;
                      });
                    }
                    
                    // Create new effect instance
                    const newEffect: EffectInstance = {
                      id: `${type}-${slot}-${Date.now()}-${Math.random().toString(36).substring(2, 11)}`,
                      type,
                      parameters: defaultParams,
                      bypassed: false,
                      slot,
                    };
                    
                    // Replace or add effect in this slot
                    const updatedChain = effectsChain.filter(e => e.slot !== slot);
                    onEffectsChange([...updatedChain, newEffect].sort((a, b) => a.slot - b.slot));
                  }}
                  onParameterChange={(parameterId: string, value: number) => {
                    if (!effect) return;
                    
                    const updatedChain = effectsChain.map(e =>
                      e.id === effect.id
                        ? { ...e, parameters: { ...e.parameters, [parameterId]: value } }
                        : e
                    );
                    onEffectsChange(updatedChain);
                  }}
                  onParametersChange={(parameters: Record<string, number>) => {
                    if (!effect) return;
                    
                    const updatedChain = effectsChain.map(e =>
                      e.id === effect.id
                        ? { ...e, parameters: { ...e.parameters, ...parameters } }
                        : e
                    );
                    onEffectsChange(updatedChain);
                  }}
                  onBypass={(bypassed: boolean) => {
                    if (!effect) return;
                    
                    const updatedChain = effectsChain.map(e =>
                      e.id === effect.id ? { ...e, bypassed } : e
                    );
                    onEffectsChange(updatedChain);
                  }}
                  onRemove={() => {
                    if (!effect) return;
                    
                    const updatedChain = effectsChain.filter(e => e.id !== effect.id);
                    onEffectsChange(updatedChain);
                  }}
                />
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
